package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.painter.Painter // Імпорт Painter
import androidx.compose.ui.graphics.takeOrElse
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.atoms.inputs.*
import com.patronat.top.ui.theme.*

@Composable
fun AppInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    labelTextStyle: TextStyle = InputLabelText,
    placeholder: String? = null,
    hint: String? = null,
    forceState: InputState? = null,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    leadingIcon: Painter? = null,
    trailingIcon: Painter? = null,
    inputMode: AppInputMode = AppInputMode.Text,
    defaultPhoneCountryCode: String = DefaultPhoneCountryCode,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
) {

    val interactionSource = remember { MutableInteractionSource() }
    val isFocused by interactionSource.collectIsFocusedAsState()

    // Визначаємо поточний стан
    val currentState = when {
        forceState != null -> forceState
        !enabled -> InputState.Disabled
        readOnly -> InputState.ReadOnly
        isFocused -> InputState.Active
        value.isNotEmpty() -> InputState.Filled
        else -> InputState.Default
    }

    // Отримуємо стилі для цього стану
    val styles = getInputStyles(currentState)
    val isPhoneMode = inputMode == AppInputMode.Phone
    val phonePickerState = if (isPhoneMode) {
        rememberPhonePickerState(defaultCountryCode = defaultPhoneCountryCode)
    } else {
        null
    }
    val phoneCountries = if (isPhoneMode) {
        phonePickerState?.countryList.orEmpty()
    } else {
        emptyList()
    }
    val fallbackCountryCode = phonePickerState?.countryCode ?: defaultPhoneCountryCode

    val resolvedPhoneValue = remember(value, phoneCountries, fallbackCountryCode, isPhoneMode) {
        if (!isPhoneMode) {
            ResolvedPhoneValue(dialCode = "", localNumber = value)
        } else {
            resolvePhoneValue(
                rawValue = value,
                countries = phoneCountries,
                fallbackCountryCode = fallbackCountryCode
            )
        }
    }

    val currentDialCode = if (isPhoneMode) {
        resolvedPhoneValue.dialCode.ifBlank { phonePickerState?.getCountryPhoneCode().orEmpty() }
    } else {
        ""
    }

    val maxLocalDigits = remember(currentDialCode) {
        if (currentDialCode.isBlank()) {
            15
        } else {
            (15 - currentDialCode.filter(Char::isDigit).length).coerceAtLeast(4)
        }
    }

    val renderedValue = if (isPhoneMode) resolvedPhoneValue.localNumber else value
    val renderedPlaceholder = if (isPhoneMode) {
        placeholder
            ?.let {
                resolvePhoneValue(
                    rawValue = it,
                    countries = phoneCountries,
                    fallbackCountryCode = fallbackCountryCode
                ).localNumber
            }
            ?.takeIf { it.isNotBlank() }
            ?: "00 000 00 00"
    } else {
        placeholder
    }

    val effectiveKeyboardOptions = if (isPhoneMode) {
        keyboardOptions.copy(keyboardType = KeyboardType.Phone)
    } else {
        keyboardOptions
    }

    val effectiveLeadingIcon = if (isPhoneMode) null else leadingIcon

    Column(modifier = modifier.fillMaxWidth()) {

        // Заголовок (Label)
        if (label != null) {
            InputLabel(
                text = label,
                textStyle = labelTextStyle
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        // Поле вводу
        BaseTextField(
            value = renderedValue,
            onValueChange = { newValue ->
                if (isPhoneMode) {
                    val localDigits = newValue.filter(Char::isDigit).take(maxLocalDigits)
                    onValueChange(combinePhone(currentDialCode, localDigits))
                } else {
                    onValueChange(newValue)
                }
            },
            styles = styles,
            placeholder = renderedPlaceholder,
            enabled = enabled,
            readOnly = readOnly,
            leadingContent = if (isPhoneMode && phonePickerState != null) {
                {
                    PhoneCountryCodePicker(
                        state = phonePickerState,
                        rawValue = value,
                        enabled = enabled,
                        readOnly = readOnly,
                        textColor = styles.textColor.color.takeOrElse { MineShaft950 },
                        onDialCodeChanged = { selectedDialCode ->
                            if (selectedDialCode == currentDialCode) return@PhoneCountryCodePicker
                            val newMaxLocalDigits = (15 - selectedDialCode.filter(Char::isDigit).length)
                                .coerceAtLeast(4)
                            val localDigits = resolvedPhoneValue.localNumber.take(newMaxLocalDigits)
                            val updatedValue = combinePhone(selectedDialCode, localDigits)
                            if (updatedValue != value) {
                                onValueChange(updatedValue)
                            }
                        }
                    )
                }
            } else {
                null
            },
            leadingIcon = effectiveLeadingIcon,
            trailingIcon = trailingIcon,
            interactionSource = interactionSource,
            keyboardOptions = effectiveKeyboardOptions,
            keyboardActions = keyboardActions
        )

        // Повідомлення знизу (Hint / Error)
        if (hint != null) {
            Spacer(modifier = Modifier.height(4.dp))
            val messageType = if (currentState == InputState.Error) InputMessageType.Error else InputMessageType.Alert
            InputMessage(
                text = hint,
                type = messageType
            )
        }
    }
}

@Composable
private fun getInputStyles(state: InputState): InputStyles {
    val baseTextStyle = InputTextContent

    return when (state) {
        InputState.Default -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 0.5.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.Normal, color = MineShaft500),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Active -> InputStyles(
            borderColor = Primary600,
            borderWidth = 1.dp,
            backgroundColor = Primary100,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Filled -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = MineShaft900
        )
        InputState.ReadOnly -> InputStyles(
            borderColor = MineShaft200,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = MineShaft900
        )
        InputState.Error -> InputStyles(
            borderColor = AccentPink,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = AccentPink
        )
        InputState.Success -> InputStyles(
            borderColor = Primary400,
            borderWidth = 1.dp,
            backgroundColor = White,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft900),
            placeholderColor = MineShaft500,
            iconColor = Primary600
        )
        InputState.Disabled -> InputStyles(
            borderColor = MineShaft100,
            borderWidth = 0.5.dp,
            backgroundColor = MineShaft100,
            textColor = baseTextStyle.copy(fontWeight = FontWeight.SemiBold, color = MineShaft600),
            placeholderColor = MineShaft600,
            iconColor = Primary600
        )
    }
}
